import React from "react";
import { Navigate, RouteProps } from "react-router-dom";

import { ErrorView } from "components/common/ErrorView/ErrorView";
import { ErrorType } from "components/common/ErrorView/types";
import { getConfig } from "config/config";
import { QUERY_NAMING } from "config/copies/query";
import { VISION_CORE_NAMING } from "config/copies/visionCore";
import { AccountPage } from "pages/account/AccountPage";
import { AccountMembersPage } from "pages/accountMembers/AccountMembersPage";
import { AdminExportDataPage } from "pages/adminExportData/AdminExportDataPage";
import { DjangoAdminHelpPage } from "pages/adminHelp/DjangoAdminHelpPage";
import { AdminLabelerMetricsPage } from "pages/adminLabelerMetrics/AdminLabelerMetricsPage";
import { ApiTokensManagementPage } from "pages/apiTokensManagement/ApiTokensManagementPage";
import { DataPageRoute } from "pages/data/DataPage";
import { DemoPage } from "pages/demo/DemoPage";
import { CreatePredictorPage } from "pages/detectorCreate/CreateDetector";
import { DetectorDetailRoute } from "pages/detectorDetail/DetectorDetail";
import { DetectorGroupsPage } from "pages/detectorGroups/DetectorGroupsPage";
import { DetectorManagement } from "pages/detectorManagement/DetectorManagement";
import { DisagreementsView } from "pages/disagreement/Disagreements";
import { HubsDetailPageRoute } from "pages/hubsDetail/HubsDetailPage";
import { HubsManagementPage } from "pages/hubsManagement/HubsManagementPage";
import { HubStatusPageRoute } from "pages/hubsStatus/HubStatusPage";
import { InspectionDetailRoute, ListInspectionsRoute } from "pages/inspections/ListInspections";
import { InvitationAcceptancePage } from "pages/invitationAcceptancePage/InvitationAcceptancePage";
import { LabelersManagement } from "pages/labelersManagement/LabelersManagement";
import { PosicheckInfoPage } from "pages/posicheckInfo/PosicheckInfoPage";
import { ReviewPageRoute } from "pages/review/ReviewPage";
import { LogOutRoute } from "routing/AuthRoutes";
import { TermsOfService } from "routing/TermsOfService";
import { UserProps } from "types/users";

import { TabChoices } from "./constants";

const { REACT_APP_BASE_PREFIX } = getConfig();

/**
 * Find the default path for a given tab and subtab. If multiple paths have the same tab
 * and subtab, use the first in the list as the default.
 */
export function tabToPath(tab?: TabChoices, subTab?: string) {
  let path = REACT_APP_BASE_PREFIX;

  if (tab) {
    if (!subTab) {
      path += PROTECTED_ROUTE_SPECS.find((spec) => spec.tab === tab)?.path || "";
    } else {
      path +=
        PROTECTED_ROUTE_SPECS.find((spec) => spec.tab === tab && spec.subTab === subTab)?.path ||
        PROTECTED_ROUTE_SPECS.find((spec) => spec.tab === tab)?.path ||
        "";
    }
  } else {
    path += "/";
  }

  return path;
}

export interface RouteSpec {
  // Name of the webpage
  name: string;
  // Relative path (without the BASE_PREFIX)
  path: string;
  // Other paths that resolve to the same Route (e.g., for maintaining backwards-compatible links or redirects)
  pathAliases?: string[];
  // What react component will be rendered?
  component: (props: RouteProps & UserProps) => JSX.Element;
  // Is this page limited to admin users only?
  admin?: boolean;
  // Does the URL need to exactly match `path`, or just the prefix?
  exact?: boolean;
  // Which top-level tab does this page belong to?
  tab?: TabChoices;
  // Which sub-tab does this page belong to?
  subTab?: string;
  // Is this route exempt from the terms-of-service gate?
  tos_exempt?: boolean;
  // Can a labeler view this page?
  labeler_can_view?: boolean;
  // Should this page be limited only to labelers?
  only_labeler_can_view?: boolean;
  // Badge to display on the route
  badge?: string;
}

export const PUBLIC_ROUTE_SPECS = [
  {
    name: "Logout",
    path: "/logout",
    component: LogOutRoute,
    exact: true,
    tos_exempt: true, // You need to be able to logout
    labeler_can_view: true, // Labelers need to be able to logout
  },
  {
    name: "Accept Invitation",
    path: "/account/accept-invitation/:token",
    component: InvitationAcceptancePage,
    exact: true,
  },
];

/**
 * Add new routes to the webpage here!
 */
export const PROTECTED_ROUTE_SPECS: Array<RouteSpec> = [
  // Detectors routes
  {
    name: "Home",
    path: "/",
    component: DetectorManagement,
    exact: true,
    tab: TabChoices.Detectors,
  },
  {
    name: "Detectors",
    path: "/detectors",
    component: DetectorManagement,
    exact: true,
    tab: TabChoices.Detectors,
  },
  {
    name: "Detectors",
    path: "/detectors/groups",
    component: DetectorGroupsPage,
    exact: true,
  },
  {
    name: "Detector Detail",
    path: "/detectors/:id",
    component: DetectorDetailRoute,
    exact: true,
    tab: TabChoices.Detectors,
  },
  {
    name: "Detector Pipeline Details",
    path: "/detectors/:id/pipelinedetails",
    component: DetectorDetailRoute,
    exact: true,
    tab: TabChoices.Detectors,
  },
  {
    name: "Create Detectors",
    path: "/detectors/create",
    component: CreatePredictorPage,
    exact: true,
    tab: TabChoices.Detectors,
  },
  // Review routes
  {
    name: "Review",
    path: "/review/queue",
    component: ReviewPageRoute,
    exact: true,
    tab: TabChoices.Review,
    labeler_can_view: true,
  },
  {
    name: "Review Specific Detector",
    path: "/review/queue/detector/:detectorId",
    component: ReviewPageRoute,
    exact: true,
    tab: TabChoices.Review,
    labeler_can_view: true,
  },
  // Data routes
  {
    name: "Data",
    path: `/data/${QUERY_NAMING.kebabCasePlural}`,
    pathAliases: ["/data", "/data/posichecks"],
    component: DataPageRoute,
    exact: true,
    tab: TabChoices.Data,
    subTab: `${QUERY_NAMING.uiNamePlural}`,
  },
  {
    name: `${QUERY_NAMING.uiName} Info`,
    path: `/data/${QUERY_NAMING.kebabCasePlural}/:id`,
    pathAliases: ["/data/posichecks/:id"],
    component: PosicheckInfoPage,
    exact: true,
    tab: TabChoices.Data,
    subTab: `${QUERY_NAMING.uiName} Info`,
    labeler_can_view: true, // Specific permissions will be enforced on the server
  },
  {
    name: "Inspections",
    path: "/data/inspections",
    pathAliases: ["/inspections"],
    component: ListInspectionsRoute,
    exact: true,
    tab: TabChoices.Data,
    subTab: "Inspections",
  },
  {
    name: "Inspection",
    path: "/data/inspections/:id",
    component: InspectionDetailRoute,
    exact: true,
    tab: TabChoices.Data,
    subTab: "Inspections",
  },
  // Vision Core routes. The /hubs URLs are kept for now so existing links and
  // bookmarks keep working; renaming them is tracked separately.
  {
    name: VISION_CORE_NAMING.uiNamePlural,
    path: "/hubs",
    pathAliases: ["/hubs"],
    component: HubsManagementPage,
    exact: true,
    tab: TabChoices.Hubs,
  },
  {
    name: VISION_CORE_NAMING.uiNamePlural,
    path: "/hubs/:id",
    component: HubsDetailPageRoute,
    exact: true,
    tab: TabChoices.Hubs,
  },
  {
    name: `${VISION_CORE_NAMING.uiName} Device Status`,
    path: "/hubs/:id/status",
    component: HubStatusPageRoute,
    exact: true,
    tab: TabChoices.Hubs,
  },
  // Disagreements routes
  {
    name: "Disagreements",
    path: "/disagreements",
    component: DisagreementsView,
    exact: true,
    tab: TabChoices.Disagreement,
  },
  // Labelers Management routes
  {
    name: "Labelers Management",
    path: "/labelers-management",
    component: LabelersManagement,
    exact: true,
    tab: TabChoices.Management,
  },
  // Demo routes
  {
    name: "Quick Start",
    path: "/explore/quickstart",
    pathAliases: ["/quickstart", "/explore"],
    component: DemoPage,
    exact: true,
    tab: TabChoices.Demo,
  },
  // Account routes
  {
    name: "Account",
    path: "/my-account",
    component: AccountPage,
    exact: true,
  },
  {
    name: "Account Members",
    path: "/my-account/members",
    component: AccountMembersPage,
    exact: true,
  },
  {
    name: "My API Tokens",
    path: "/my-account/api-tokens",
    component: ApiTokensManagementPage,
    exact: true,
  },
  // ADMIN ROUTES
  {
    name: "Django Admin",
    path: "/admin/django-admin-help",
    component: DjangoAdminHelpPage,
    exact: true,
    admin: true,
    tab: TabChoices.Admin,
    subTab: "Django Admin",
  },
  {
    name: `${QUERY_NAMING.uiNamePlural} Admin`,
    path: `/admin/${QUERY_NAMING.kebabCasePlural}`,
    pathAliases: ["/admin/posichecks"],
    component: DataPageRoute,
    admin: true,
    exact: true,
    tab: TabChoices.Admin,
    subTab: `${QUERY_NAMING.uiNamePlural}`,
  },
  {
    name: "Export Data",
    path: "/admin/exports",
    component: AdminExportDataPage,
    exact: true,
    admin: true,
    tab: TabChoices.Admin,
    subTab: "Export",
  },
  {
    name: "Labeler Metrics",
    path: "/admin/labeler-metrics",
    component: AdminLabelerMetricsPage,
    exact: true,
    admin: true,
    tab: TabChoices.Admin,
    subTab: "Labeler Metrics",
  },
  // SPECIAL ROUTES
  {
    name: "Terms of Service",
    path: "/tos",
    component: TermsOfService,
    exact: true,
    tos_exempt: true, // You need to be able to access this page to accept the terms!
    labeler_can_view: true, // Labelers need to accept terms, too
  },
  // FINAL REDIRECTION (/*)
  {
    name: "Labeler Redirect",
    path: "/*", // All other pages redirect to the review queue
    component: () => <Navigate replace to="/reef/review/queue" />,
    labeler_can_view: true,
    only_labeler_can_view: true, // We only want labelers to have this page redirect
  },
  {
    name: "Page Not Found",
    path: "/*",
    component: () => <ErrorView error={ErrorType.ERROR_404} />,
    labeler_can_view: true,
  },
];
